/**
 * batchStatuses — reading a SharePoint $batch response's per-operation result.
 *
 * Its own zero-import module for the reason snapshotPolicy and batchCodes are:
 * ProductionGridService drags in MSAL config, so nothing importing it can be
 * unit-tested — and this rule decides whether a write is retried, which is the
 * difference between a hundred rows rewritten one at a time and one.
 *
 * THE FAILURE IT ENCODES. A $batch changeset is atomic, so a chunk of a
 * hundred updates sent as ONE changeset fails entirely when a single row is
 * refused; the caller then rewrote all hundred singly, at a CORS preflight and
 * ~250ms each, retried up to ten times apiece under throttling. Updates now go
 * one changeset per row, and this says which of them to rewrite.
 */

/** HTTP statuses a SharePoint write answers with on success. */
const WROTE = new Set([200, 201, 204]);

/**
 * The operation indexes that did NOT succeed, given the statuses parsed out of
 * a batch response in request order.
 *
 * A response that does not line up with the request — fewer statuses than
 * operations, or more — reports EVERY operation as unwritten. An answer we
 * cannot read is not evidence that the writes landed, and the caller's retry
 * is idempotent, so the safe reading of an unreadable response is "none of
 * this happened".
 */
export function rowsToRetry(statuses: readonly number[], operations: number): number[] {
  if (operations <= 0) return [];
  if (statuses.length !== operations) return Array.from({ length: operations }, (_, i) => i);
  return statuses.flatMap((s, i) => (WROTE.has(s) ? [] : [i]));
}

/** The distinct statuses behind a retry list, for saying WHY in one line. */
export function refusedStatuses(statuses: readonly number[], retry: readonly number[]): number[] {
  return [...new Set(retry.map(i => statuses[i]).filter(s => typeof s === "number"))];
}

/**
 * Statuses where sending the row again, by itself, can plausibly succeed.
 *
 * Throttling and a buckling server are the whole reason the single-row path
 * exists: the row was fine, the moment was not. Everything else — a malformed
 * body, a column the list does not have, a denied token, a row somebody
 * deleted — answers the retry with the identical refusal.
 */
const TRANSIENT = new Set([408, 429, 500, 502, 503, 504]);

/**
 * The status behind a refusal that rewriting cannot fix, or `null`.
 *
 * Updates go one changeset per row, so rows fail INDEPENDENTLY. When every
 * row in a chunk is refused with the same non-transient status, the cause is
 * therefore not the rows — it is something they share: the entity type, a
 * field the list does not have, the token. Rewriting a hundred of those one
 * at a time is a hundred CORS preflights and PATCHes, each retried up to ten
 * times under throttling, to collect the same hundred refusals. That storm
 * saturates the browser's connection pool, and the reads the screen is
 * waiting on die behind it with ERR_HTTP2_PROTOCOL_ERROR — which is what
 * turns one bad import into a Production Grid that will not load.
 *
 * Deliberately narrow, because skipping a retry that would have worked loses
 * a write. All three conditions must hold:
 *
 *   - the response lined up with the request, so the statuses can be trusted.
 *     An unreadable response is NOT systemic: we cannot see a shared cause,
 *     and the safe reading of an answer we cannot parse is still "retry".
 *   - every operation was refused. One survivor proves the rest are per-row.
 *   - they all share one status, and it is not transient.
 */
export function systemicRefusal(
  statuses: readonly number[], retry: readonly number[], operations: number,
): number | null {
  if (operations <= 0) return null;
  if (statuses.length !== operations) return null;
  if (retry.length !== operations) return null;

  const distinct = refusedStatuses(statuses, retry);
  if (distinct.length !== 1) return null;

  const status = distinct[0];
  return TRANSIENT.has(status) ? null : status;
}

/**
 * The first SharePoint error message inside a $batch response body, or `null`.
 *
 * postBatch used to return status codes alone, so a chunk refused for a named,
 * fixable reason ("Column 'X' does not exist") reached the console as the
 * number 400 and nothing else. The status says a write was refused; only this
 * says what to go and fix.
 */
export function firstBatchError(body: string): string | null {
  const m = /"message"\s*:\s*(?:\{[^{}]*?"value"\s*:\s*"((?:[^"\\]|\\.)*)"|"((?:[^"\\]|\\.)*)")/.exec(body);
  const raw = m?.[1] ?? m?.[2];
  if (raw === undefined) return null;

  let text: string;
  try {
    text = JSON.parse(`"${raw}"`) as string;
  } catch {
    text = raw;
  }
  text = text.trim();
  return text === "" ? null : text.slice(0, 300);
}
